import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Spinner,
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import React from "react";

interface DialogBoxProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    children: React.ReactNode;
    primaryButtonText?: string;
    primaryButtonAppearance?: 'primary' | 'secondary';
    onPrimaryButtonClick?: () => void;
    isPrimaryButtonDisabled?: boolean;
    isPrimaryButtonLoading?: boolean;
    showSecondaryButton?: boolean;
    secondaryButtonText?: string;
    onSecondaryButtonClick?: () => void;
    isSecondaryButtonDisabled?: boolean;
    className?: string;
    hideCloseButton?: boolean;
}

export const DialogBox: React.FC<DialogBoxProps> = ({
    open,
    onOpenChange,
    title,
    children,
    primaryButtonText = 'Confirm',
    primaryButtonAppearance = 'primary',
    onPrimaryButtonClick,
    isPrimaryButtonDisabled,
    isPrimaryButtonLoading,
    showSecondaryButton = true,
    secondaryButtonText = 'Cancel',
    onSecondaryButtonClick,
    isSecondaryButtonDisabled,
    className,
    hideCloseButton = false
}) => {
    const formRef = React.useRef<HTMLFormElement>(null);

    const handlePrimaryButtonClick = () => {
        if (formRef.current) {
            // If there's a form, validate it first
            const isValid = formRef.current.checkValidity();
            formRef.current.reportValidity();
            
            if (!isValid) return;
        }
        
        // If no form or form is valid, proceed with the click handler
        onPrimaryButtonClick?.();
    };

    return (
        <Dialog 
            open={open} 
            onOpenChange={(_, data) => !hideCloseButton && !isPrimaryButtonLoading && onOpenChange(data.open)}
        >
            <DialogSurface className={className}>
                <DialogBody>
                    <DialogTitle action={
                        hideCloseButton ? undefined : (
                            <DialogTrigger action="close">
                                <Button appearance="subtle" icon={<Dismiss20Regular />} disabled={isPrimaryButtonLoading} />
                            </DialogTrigger>
                        )
                    }>
                        {title}
                    </DialogTitle>
                    <DialogContent>
                        {React.Children.map(children, child => {
                            if (React.isValidElement(child) && child.type === 'form') {
                                return React.cloneElement(child as React.ReactElement<{ ref?: React.RefObject<HTMLFormElement> }>, { ref: formRef });
                            }
                            return child;
                        })}
                    </DialogContent>
                    <DialogActions>
                        {onPrimaryButtonClick && (
                            <Button
                                appearance={primaryButtonAppearance}
                                onClick={handlePrimaryButtonClick}
                                disabled={isPrimaryButtonDisabled || isPrimaryButtonLoading}
                            >
                                {isPrimaryButtonLoading ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: "10px" }}><Spinner size="tiny" /> Loading...</div>
                                ) : (
                                    primaryButtonText
                                )}
                            </Button>
                        )}
                        {showSecondaryButton && (
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    appearance="secondary"
                                    onClick={onSecondaryButtonClick}
                                    disabled={isSecondaryButtonDisabled || isPrimaryButtonLoading}
                                >
                                    {secondaryButtonText}
                                </Button>
                            </DialogTrigger>
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
